import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import Button, { EditButton } from '@components/Button';

import { editAgencyCRMAuthentication, fetchAgencyCRMAuthentication, fetchAgencyProductConfigs } from '@actions/agency';

import CitizenAuth0Modal from '../../CitizenAuth0Modal';
import SSOModal from '../../SSOModal';

import './style.scss';

export interface ICitizenAuthenticationProps {
  agencySettings: IAgencySettings;
  productConfigurations: IProductConfigurationsResponse;
  crmAuthentication: IAgencyCRMAuthResponse;
  fetchAgencyCRMAuthentication: TFetchAgencyCRMAuthentication;
  editAgencyCRMAuthentication: TEditAgencyCRMAuthentication;
  fetchAgencyProductConfigs: TFetchAgencyProductConfigs;
}

export interface ICitizenAuthenticationState {
  crmAuthentication: IAgencyCRMAuthResponse;
  isSSOModalOpen: boolean;
  isCitizenAuth0ModalOpen: boolean;
}

export class CitizenAuthentication extends React.Component<ICitizenAuthenticationProps, ICitizenAuthenticationState> {
  readonly timezoneOptions = [TimeZone.HST, TimeZone.AKST, TimeZone.PST, TimeZone.CST, TimeZone.EST, TimeZone.MST];

  constructor(props: ICitizenAuthenticationProps) {
    super(props);
    this.state = this.getEmptyState();
  }

  componentDidMount() {
    this.props.fetchAgencyCRMAuthentication(this.props.agencySettings.id);

    if (!this.props.productConfigurations) {
      this.props.fetchAgencyProductConfigs(this.props.agencySettings.id);
    }
  }

  componentDidUpdate(prevProps: Readonly<ICitizenAuthenticationProps>): void {
    if (prevProps.crmAuthentication !== this.props.crmAuthentication) {
      this.setState({
        crmAuthentication: this.props.crmAuthentication,
      });
    }
  }

  getEmptyState = (): ICitizenAuthenticationState => ({
    crmAuthentication: null,
    isSSOModalOpen: false,
    isCitizenAuth0ModalOpen: false,
  });

  renderEmptyProductContainer = (product: string, onClick: () => void) => (
    <div onClick={onClick} className="empty-product">
      <i />
      {product}
    </div>
  );

  toggleSSOModal = () => {
    this.setState((prevState) => ({
      isSSOModalOpen: !prevState.isSSOModalOpen,
    }));
  };

  toggleCitizenAuth0Modal = () => {
    this.setState((prevState) => ({
      isCitizenAuth0ModalOpen: !prevState.isCitizenAuth0ModalOpen,
    }));
  };

  renderActivatedSSO = () => {
    const {
      sso: { isActive, label },
    } = this.state.crmAuthentication;

    return (
      <div className={classnames('product-container__integration')}>
        <div className={classnames('integration-info__header')}>
          <h4>CRM/Citizen Authentication: Beverly Hills SSO</h4>
          <h4
            className={classnames('integration-info__status', {
              'integration-info__status--active': isActive,
              'integration-info__status--inactive': !isActive,
            })}
          >
            {isActive ? 'Active' : 'Inactive'}
          </h4>
          <EditButton onClick={this.toggleSSOModal} />
        </div>
        <div className={classnames('agency-form__product-configs-container')}>
          <div className={classnames('agency-form__product-config')}>
            <h5>Integration Name</h5>
            <span className={classnames('integration-value')}>{label}</span>
          </div>
        </div>
      </div>
    );
  };

  renderActivatedCitizenAuth0 = () => {
    const {
      citizenAuth0: { isActive },
    } = this.state.crmAuthentication;

    return (
      <div className={classnames('product-container__integration')}>
        <div className={classnames('integration-info__header')}>
          <h4>CRM/Citizen Authentication: Auth0-SSO</h4>
          <h4
            className={classnames('integration-info__status', {
              'integration-info__status--active': isActive,
              'integration-info__status--inactive': !isActive,
            })}
          >
            {isActive ? 'Active' : 'Inactive'}
          </h4>
          <EditButton onClick={this.toggleCitizenAuth0Modal} />
        </div>
      </div>
    );
  };

  onSSOSubmit = (ssoRequest) => {
    this.setState((prevState) => ({
      crmAuthentication: {
        ...prevState.crmAuthentication,
        sso: ssoRequest,
      },
      isSSOModalOpen: false,
    }));
  };

  onCitizenAuth0Submit = (citizenAuth0Request) => {
    this.setState((prevState) => ({
      crmAuthentication: {
        ...prevState.crmAuthentication,
        citizenAuth0: citizenAuth0Request,
      },
      isCitizenAuth0ModalOpen: false,
    }));
  };

  getRequestFromState = (): IAgencyCRMAuthEditRequest => {
    const { citizenAuth0, sso } = this.state.crmAuthentication;

    const agencyRequest: IAgencyCRMAuthEditRequest = {};

    if (citizenAuth0) {
      agencyRequest.citizenAuth0 = citizenAuth0;
    }
    if (sso) {
      agencyRequest.sso = sso;
    }

    return agencyRequest;
  };

  onSubmit = () => {
    const { id: agencyId } = this.props.agencySettings;
    const editRequest = this.getRequestFromState();
    this.props.editAgencyCRMAuthentication(agencyId, editRequest);
  };

  render() {
    const { sso, citizenAuth0 } = this.state.crmAuthentication ?? {};

    const { crm } = this.props.productConfigurations ? this.props.productConfigurations : { crm: null };

    return (
      <div className="agency-products-listing">
        <div className="product-container">
          {crm?.isActive ? (
            <>
              <div className="product-container">
                {sso
                  ? this.renderActivatedSSO()
                  : this.renderEmptyProductContainer(
                      'CRM/Citizen Authentication: Beverly Hills SSO',
                      this.toggleSSOModal
                    )}
              </div>
              <div className="product-container">
                {citizenAuth0
                  ? this.renderActivatedCitizenAuth0()
                  : this.renderEmptyProductContainer(
                      'CRM/Citizen Authentication: Auth0-SSO',
                      this.toggleCitizenAuth0Modal
                    )}
              </div>
            </>
          ) : (
            <h3>Product CRM is not active</h3>
          )}
        </div>

        <div className="agency-setting__footer">
          <Button bsStyle={ButtonStyle.DEFAULT}>Cancel</Button>
          <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onSubmit}>
            Save Changes
          </Button>
        </div>

        <CitizenAuth0Modal
          citizenAuth0={this.state.crmAuthentication?.citizenAuth0 ? this.state.crmAuthentication.citizenAuth0 : null}
          show={this.state.isCitizenAuth0ModalOpen}
          onClose={this.toggleCitizenAuth0Modal}
          onSubmit={this.onCitizenAuth0Submit}
        />

        <SSOModal
          sso={this.state.crmAuthentication?.sso ? this.state.crmAuthentication.sso : null}
          show={this.state.isSSOModalOpen}
          onClose={this.toggleSSOModal}
          onSubmit={this.onSSOSubmit}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  agencySettings: state.agency.agencySettings,
  crmAuthentication: state.agency.crmAuthentication,
  productConfigurations: state.agency.productConfigurations,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchAgencyCRMAuthentication: (agencyId: number) => dispatch(fetchAgencyCRMAuthentication(agencyId)),
  editAgencyCRMAuthentication: (agencyId: number, editRequest: IAgencyCRMAuthEditRequest) =>
    dispatch(editAgencyCRMAuthentication(agencyId, editRequest)),
  fetchAgencyProductConfigs: (agencyId: number) => dispatch(fetchAgencyProductConfigs(agencyId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CitizenAuthentication);
